import { useState, useEffect } from 'react';
import { matchDetailsService, MatchDetails } from '../services/matchDetails';

export const useMatchDetails = (matchId: string | undefined) => {
  const [matchDetails, setMatchDetails] = useState<MatchDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatchDetails = async () => {
    console.log('useMatchDetails - fetchMatchDetails called with matchId:', matchId);
    
    if (!matchId) {
      console.log('useMatchDetails - No match ID provided');
      setError('No match ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await matchDetailsService.getMatchDetails(matchId);
      
      if (data) {
        setMatchDetails(data);
      } else {
        setError('Failed to fetch match details');
      }
    } catch (err) {
      setError('Error fetching match details');
      console.error('Error in useMatchDetails:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchDetails();
  }, [matchId]);

  // Listen for match score updates but don't refresh entire page
  // Real-time scores are handled by useRealtimeScores hook
  useEffect(() => {
    const handleMatchScoresUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.matchId === matchId) {
        console.log('🔄 Match scores updated - real-time scores will handle UI update');
        // Don't call fetchMatchDetails() to avoid page refresh
        // The useRealtimeScores hook will handle the score updates
      }
    };

    window.addEventListener('matchScoresUpdated', handleMatchScoresUpdated);
    
    return () => {
      window.removeEventListener('matchScoresUpdated', handleMatchScoresUpdated);
    };
  }, [matchId]);

  const updateMatchDetails = async (updates: Partial<MatchDetails>) => {
    if (!matchId) return false;

    try {
      const success = await matchDetailsService.updateMatchDetails(matchId, updates);
      if (success && matchDetails) {
        setMatchDetails({ ...matchDetails, ...updates });
      }
      return success;
    } catch (err) {
      console.error('Error updating match details:', err);
      return false;
    }
  };

  const updateOfficials = async (officials: any[]) => {
    if (!matchId) return false;

    try {
      const success = await matchDetailsService.updateMatchOfficials(matchId, officials);
      if (success && matchDetails) {
        setMatchDetails({ ...matchDetails, match_officials: officials });
      }
      return success;
    } catch (err) {
      console.error('Error updating officials:', err);
      return false;
    }
  };

  return {
    matchDetails,
    loading,
    error,
    updateMatchDetails,
    updateOfficials,
    refetch: fetchMatchDetails
  };
};
